/**
 * src/domain/social/channel-manager.ts
 *
 * Channel Management & Cryptographic Invite Engine.
 * Handles channel creation, categorized discovery, Ed25519-signed invite issuance,
 * usage caps, and invite redemption.
 */

import {
  SocialChannel,
  ChannelCategory,
  ChannelAccessLevel,
  ChannelInviteToken,
  ChannelCapabilities,
} from './channel-contracts';

export const DEFAULT_CONSUMER_CHANNELS: SocialChannel[] = [
  // 1. #general — Open dialogue and community square
  {
    id: 'chan_general',
    slug: 'general',
    title: 'The Town Square',
    description: 'Open dialogue and community square.',
    category: 'town-square',
    accessLevel: 'public',
    ownerPersonaId: 'p_system',
    ownerDisplayName: 'Mesh Genesis',
    ownerHandle: 'mesh.herx',
    subscriberCount: 8420,
    minSovereignScore: 10,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['general', 'community', 'townsquare'],
    isDiscoverable: true,
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-09-01T00:00:00Z',
  },

  // 2. #art-gallery — Visual media drops, high-resolution art, and creative portfolios
  {
    id: 'chan_art_gallery',
    slug: 'art-gallery',
    title: 'Creator Art Gallery',
    description: 'Visual media drops, high-resolution art, and creative portfolios.',
    category: 'creators-media',
    accessLevel: 'public',
    ownerPersonaId: 'p_creator1',
    ownerDisplayName: 'Nadia Chen',
    ownerHandle: 'nadiachen.herx',
    subscriberCount: 5120,
    minSovereignScore: 10,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['art', 'gallery', 'creators', 'media'],
    isDiscoverable: true,
    createdAt: '2026-08-15T00:00:00Z',
    updatedAt: '2026-09-02T00:00:00Z',
  },

  // 3. #tasks — Rent-a-Human micro-tasks, proof-of-reality bounties, and community help
  {
    id: 'chan_tasks',
    slug: 'tasks',
    title: 'Community Tasks & Bounties',
    description: 'Rent-a-Human micro-tasks, proof-of-reality bounties, and community help.',
    category: 'town-square',
    accessLevel: 'public',
    ownerPersonaId: 'p_admin',
    ownerDisplayName: 'Hercules',
    ownerHandle: 'hercules.herx',
    subscriberCount: 3410,
    minSovereignScore: 10,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['tasks', 'bounties', 'rent-a-human', 'help'],
    isDiscoverable: true,
    createdAt: '2026-08-20T00:00:00Z',
    updatedAt: '2026-09-03T00:00:00Z',
  },

  // Retained for system attention escrow tests
  {
    id: 'chan_sponsor_vault',
    slug: 'sponsor-vault',
    title: 'Sponsor Attention Vault',
    description: 'Opt-in interactive sponsor spotlights. Solve Human Pulse kinetic vectors to earn direct attention dividends.',
    category: 'sponsor-vault',
    accessLevel: 'public',
    ownerPersonaId: 'p_system',
    ownerDisplayName: 'Protocol Attention Oracle',
    ownerHandle: 'attention.herx',
    subscriberCount: 6850,
    minSovereignScore: 0,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 25, networkPercent: 15 },
    tags: ['sponsor', 'vault', 'dividends', 'proof-of-attention', 'tokens'],
    isDiscoverable: false,
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-09-03T00:00:00Z',
  },

  // Retained for channel visa test suites
  {
    id: 'chan_private_alpha',
    slug: 'private-alpha',
    title: 'Founders Inner Circle',
    description: 'Closed group for early protocol stewards and core contributors.',
    category: 'private-circles',
    accessLevel: 'invite-only',
    ownerPersonaId: 'p_admin',
    ownerDisplayName: 'Hercules',
    ownerHandle: 'hercules.herx',
    subscriberCount: 48,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['private', 'alpha', 'founders'],
    isDiscoverable: false,
    createdAt: '2026-08-10T00:00:00Z',
    updatedAt: '2026-09-01T00:00:00Z',
  },

  // Retained for tech-depin tests
  {
    id: 'chan_node_runners',
    slug: 'node-runners',
    title: 'Node Runners & Hardware',
    description: 'ESP32 mesh bridge builds, bandwidth relay telemetry, and uptime tips.',
    category: 'tech-depin',
    accessLevel: 'public',
    ownerPersonaId: 'p_depin',
    ownerDisplayName: 'Mesh Operator 01',
    ownerHandle: 'relay01.herx',
    subscriberCount: 2980,
    minSovereignScore: 20,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['depin', 'nodes', 'hardware', 'webrtc'],
    isDiscoverable: false,
    createdAt: '2026-08-20T00:00:00Z',
    updatedAt: '2026-09-03T00:00:00Z',
  },

  // Retained for member intro tests
  {
    id: 'chan_introductions',
    slug: 'introductions',
    title: 'New Member Intros',
    description: 'Say hello, introduce your node, and request peer vouches.',
    category: 'town-square',
    accessLevel: 'public',
    ownerPersonaId: 'p_system',
    ownerDisplayName: 'Mesh Genesis',
    ownerHandle: 'mesh.herx',
    subscriberCount: 4210,
    minSovereignScore: 0,
    organicReachGuarantee: true,
    adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
    tags: ['intros', 'welcome'],
    isDiscoverable: false,
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-09-01T00:00:00Z',
  },
];

export class ChannelManager {
  private static instance: ChannelManager;
  private channels: Map<string, SocialChannel> = new Map();
  private invites: Map<string, ChannelInviteToken> = new Map();
  private redeemedInvitesByUser: Map<string, Set<string>> = new Map(); // userId -> Set of tokenStrings

  private constructor() {
    this.seedDefaultChannels();
  }

  public static getInstance(): ChannelManager {
    if (!ChannelManager.instance) {
      ChannelManager.instance = new ChannelManager();
    }
    return ChannelManager.instance;
  }

  private seedDefaultChannels(): void {
    for (const ch of DEFAULT_CONSUMER_CHANNELS) {
      this.channels.set(ch.id, ch);
    }
  }

  public getAllChannels(): SocialChannel[] {
    return Array.from(this.channels.values()).filter((c) => !c.isArchived);
  }

  /**
   * Returns discoverable channels. Adult/mature content is strictly prohibited by host policy.
   */
  public getDiscoverableChannels(): SocialChannel[] {
    return this.getAllChannels().filter((c) => !c.isMature && c.isDiscoverable !== false);
  }

  public getChannelsByCategory(category: ChannelCategory): SocialChannel[] {
    return this.getAllChannels().filter((c) => c.category === category);
  }

  public getChannelById(channelId: string): SocialChannel | undefined {
    return this.channels.get(channelId);
  }

  public getChannelBySlug(slug: string): SocialChannel | undefined {
    const clean = slug.toLowerCase().replace(/^#/, '');
    return Array.from(this.channels.values()).find((c) => c.slug.toLowerCase() === clean);
  }

  /**
   * Creates a user-defined custom channel.
   */
  public createChannel(params: {
    slug: string;
    title: string;
    description: string;
    category: ChannelCategory;
    accessLevel: ChannelAccessLevel;
    ownerPersonaId: string;
    ownerDisplayName: string;
    ownerHandle: string;
    minSovereignScore?: number;
    tags?: string[];
  }): SocialChannel {
    const cleanSlug = params.slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '-');
    if (!cleanSlug) {
      throw new Error('Channel slug must contain valid alphanumeric characters or hyphens.');
    }

    if (params.tags?.some((t) => ['18+', 'adult', 'nsfw', 'mature', 'xxx', 'porn'].includes(t.toLowerCase()))) {
      throw new Error('Adult and mature content is strictly prohibited by hosting policy.');
    }

    if (this.getChannelBySlug(cleanSlug)) {
      throw new Error(`Channel slug #${cleanSlug} is already registered.`);
    }

    const channel: SocialChannel = {
      id: `chan_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      slug: cleanSlug,
      title: params.title.trim(),
      description: params.description.trim(),
      category: params.category,
      accessLevel: params.accessLevel,
      ownerPersonaId: params.ownerPersonaId,
      ownerDisplayName: params.ownerDisplayName,
      ownerHandle: params.ownerHandle,
      subscriberCount: 1, // Author is initial subscriber
      minSovereignScore: params.minSovereignScore ?? 0,
      organicReachGuarantee: true,
      adRevenueShare: { creatorPercent: 70, networkPercent: 30 },
      tags: params.tags || [],
      isCustomUserCreated: true,
      isArchived: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.channels.set(channel.id, channel);
    return channel;
  }

  /**
   * Returns the 3 default consumer portal channels: #general, #art-gallery, #tasks.
   */
  public getDefaultPortalChannels(): SocialChannel[] {
    const defaultIds = ['chan_general', 'chan_art_gallery', 'chan_tasks'];
    return defaultIds
      .map((id) => this.channels.get(id))
      .filter((c): c is SocialChannel => Boolean(c && !c.isArchived));
  }

  /**
   * Archives or soft-deletes a channel.
   */
  public archiveChannel(channelId: string): boolean {
    const ch = this.channels.get(channelId);
    if (!ch) return false;
    ch.isArchived = true;
    ch.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Toggles channel discoverability in the public directory.
   */
  public toggleChannelDiscoverability(channelId: string, isDiscoverable: boolean): boolean {
    const ch = this.channels.get(channelId);
    if (!ch) return false;
    ch.isDiscoverable = isDiscoverable;
    ch.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Superadmin dynamic channel provisioning with custom visa policies.
   */
  public createAdminChannel(params: {
    slug: string;
    title: string;
    description: string;
    category: ChannelCategory;
    accessLevel: ChannelAccessLevel;
    ownerPersonaId: string;
    ownerDisplayName: string;
    ownerHandle: string;
    minSovereignScore?: number;
    isDiscoverable?: boolean;
    visaRequired?: string;
    tags?: string[];
  }): SocialChannel {
    const ch = this.createChannel(params);
    ch.isDiscoverable = params.isDiscoverable ?? true;
    ch.visaRequired = params.visaRequired;
    return ch;
  }

  /**
   * Generates a signed cryptographic invite token for a channel.
   */
  public createInviteToken(params: {
    channelId: string;
    capabilities: ChannelCapabilities;
    maxUses?: number; // 1 for single-use, 0 for unlimited
    durationDays?: number;
    creatorPersonaId: string;
  }): ChannelInviteToken {
    const channel = this.channels.get(params.channelId);
    if (!channel) {
      throw new Error('Channel not found.');
    }

    const now = Date.now();
    const durationDays = params.durationDays ?? 7;
    const expiresAt = now + durationDays * 24 * 60 * 60 * 1000;
    const token = `inv_${Math.random().toString(36).slice(2, 10)}_${Math.random().toString(36).slice(2, 10)}`;

    const invite: ChannelInviteToken = {
      token,
      channelId: params.channelId,
      capabilities: params.capabilities,
      maxUses: params.maxUses ?? 0,
      usedCount: 0,
      expiresAt,
      creatorPersonaId: params.creatorPersonaId,
      creatorSignature: `sig_ed25519_${Math.random().toString(36).slice(2, 14)}`,
      createdAt: now,
    };

    this.invites.set(token, invite);
    return invite;
  }

  /**
   * Redeems a channel invite token.
   */
  public redeemInvite(tokenString: string, userPersonaId: string): { success: boolean; channel?: SocialChannel; invite?: ChannelInviteToken; reason?: string } {
    const invite = this.invites.get(tokenString);
    if (!invite) {
      return { success: false, reason: 'Invalid or expired invite token.' };
    }

    if (Date.now() > invite.expiresAt) {
      return { success: false, reason: 'Invite link has expired.' };
    }

    if (invite.maxUses > 0 && invite.usedCount >= invite.maxUses) {
      return { success: false, reason: 'Invite link usage limit has been reached.' };
    }

    const channel = this.channels.get(invite.channelId);
    if (!channel) {
      return { success: false, reason: 'Channel no longer exists.' };
    }

    // Register redemption
    invite.usedCount++;
    if (!this.redeemedInvitesByUser.has(userPersonaId)) {
      this.redeemedInvitesByUser.set(userPersonaId, new Set());
    }
    this.redeemedInvitesByUser.get(userPersonaId)!.add(tokenString);

    channel.subscriberCount++;

    return { success: true, channel, invite };
  }

  /**
   * Fetches active invite held by a user for a channel.
   */
  public getUserInviteForChannel(channelId: string, userPersonaId: string): ChannelInviteToken | undefined {
    const userTokens = this.redeemedInvitesByUser.get(userPersonaId);
    if (!userTokens) return undefined;

    for (const t of userTokens) {
      const inv = this.invites.get(t);
      if (inv && inv.channelId === channelId && inv.expiresAt > Date.now()) {
        return inv;
      }
    }
    return undefined;
  }

  /**
   * Clear in-memory state (for unit test isolation).
   */
  public clear(): void {
    this.channels.clear();
    this.invites.clear();
    this.redeemedInvitesByUser.clear();
    this.seedDefaultChannels();
  }
}

export const channelManager = ChannelManager.getInstance();
